import { useState, useEffect, useCallback, useMemo } from 'react'
import { useGet, usePost } from '../utils/apiComponent/useApi'
import { api } from '../utils/apiComponent/apiClient'
import { endpoints } from '../utils/endpoints'
import { useTranslation } from '../i18n'

export function useCategories(language = null) {
  const { language: contextLanguage } = useTranslation()
  const currentLanguage = language || contextLanguage || 'fr'
  
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Construire l'URL avec le paramètre language (l'API retourne automatiquement _count.courses)
  const categoriesUrl = useMemo(() => {
    const params = new URLSearchParams()
    // Toujours passer le paramètre language (même pour 'fr')
    params.append('language', currentLanguage)
    const queryString = params.toString()
    return `${endpoints.getCategories}?${queryString}`
  }, [currentLanguage])

  const { data: categoriesData, loading: categoriesLoading, error: categoriesError } = useGet(
    categoriesUrl
    // Par défaut, useGet charge automatiquement (immediate: true)
  )

  const { execute: createCategoryApi } = usePost(endpoints.createCategory, { immediate: false })

  useEffect(() => {
    // Utiliser uniquement les données de l'API, pas de fallback mocké
    // La nouvelle API retourne { categories: [...], total, page, totalPages }
    let categoriesArray = []
    
    if (categoriesData) {
      if (Array.isArray(categoriesData)) {
        // Ancien format (tableau direct)
        categoriesArray = categoriesData
      } else if (categoriesData.categories && Array.isArray(categoriesData.categories)) {
        // Nouveau format avec pagination
        categoriesArray = categoriesData.categories
      } else if (categoriesData.data && Array.isArray(categoriesData.data)) {
        // Format alternatif
        categoriesArray = categoriesData.data
      }
    }
    
    // Les catégories sont déjà traduites par l'API (name et description sont traduits)
    const processedCategories = categoriesArray.map(cat => ({
      ...cat,
      displayName: cat.name, // name est déjà traduit
      displayDescription: cat.description // description est déjà traduit
    }))
    
    setCategories(processedCategories)
  }, [categoriesData])

  useEffect(() => {
    setLoading(categoriesLoading)
    setError(categoriesError)
  }, [categoriesLoading, categoriesError])

  const createCategory = async (categoryData) => {
    setLoading(true)
    setError(null)
    
    try {
      // L'API attend : { name, description, icon, color }
      const response = await createCategoryApi(categoryData)

      // L'API retourne : { success: true, message: [...], data: {...} }
      if (response?.data) {
        // Ajouter la nouvelle catégorie à la liste
        setCategories(prev => [...prev, response.data])
        return response.data
      } else if (response?.success && response?.data) {
        // Format alternatif si la réponse est directement dans response
        setCategories(prev => [...prev, response.data])
        return response.data
      } else {
        throw new Error(response?.message?.[0] || "Erreur lors de la création de la catégorie")
      }
    } catch (err) {
      // Gérer les erreurs : message peut être un tableau ou une string
      const errorMessage = Array.isArray(err.response?.data?.message) 
        ? err.response.data.message[0] 
        : err.response?.data?.message || err.message || "Erreur lors de la création de la catégorie"
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  // Récupérer les catégories avec filtres
  const getCategories = useCallback(async (filters = {}) => {
    setLoading(true)
    setError(null)
    
    try {
      const params = new URLSearchParams()
      
      if (filters.page) params.append('page', filters.page.toString())
      if (filters.limit) params.append('limit', filters.limit.toString())
      if (filters.search) params.append('search', filters.search)
      // Toujours passer le paramètre language (l'API retourne automatiquement _count.courses)
      // Utiliser la langue du filtre ou la langue par défaut
      const languageToUse = filters.language || currentLanguage
      params.append('language', languageToUse)
      if (filters.sortBy) params.append('sortBy', filters.sortBy)
      if (filters.sortOrder) params.append('sortOrder', filters.sortOrder)
      
      const queryString = params.toString()
      const url = queryString ? `${endpoints.getCategories}?${queryString}` : endpoints.getCategories
      
      const response = await api.get(url)
      // La nouvelle API retourne { categories: [...], total, page, totalPages }
      // Retourner la réponse complète pour gérer la pagination
      return response?.data || response
    } catch (err) {
      const errorMessage = err?.message || "Erreur lors de la récupération des catégories"
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  // Récupérer une catégorie par ID
  const getCategory = useCallback(async (categoryId, language = null) => {
    setLoading(true)
    setError(null)
    
    try {
      const params = new URLSearchParams()
      if (language) params.append('language', language)
      
      const queryString = params.toString()
      const url = queryString 
        ? `${endpoints.getCategory(categoryId)}?${queryString}` 
        : endpoints.getCategory(categoryId)
      
      const response = await api.get(url)
      return response
    } catch (err) {
      const errorMessage = err?.message || "Erreur lors de la récupération de la catégorie"
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  // Récupérer une catégorie par nom
  const getCategoryByName = useCallback(async (name) => {
    setLoading(true)
    setError(null)
    
    try {
      const encodedName = encodeURIComponent(name)
      const response = await api.get(endpoints.categoryByName(encodedName))
      return response
    } catch (err) {
      const errorMessage = err?.message || "Erreur lors de la récupération de la catégorie"
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  // Modifier une catégorie (admin)
  const updateCategory = useCallback(async (categoryId, updateData) => {
    setLoading(true)
    setError(null)
    
    try {
      const response = await api.patch(endpoints.updateCategory(categoryId), updateData)
      return response
    } catch (err) {
      const errorMessage = err?.message || "Erreur lors de la mise à jour de la catégorie"
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  // Supprimer une catégorie (admin)
  const deleteCategory = useCallback(async (categoryId) => {
    setLoading(true)
    setError(null)
    
    try {
      const response = await api.delete(endpoints.deleteCategory(categoryId))
      return response
    } catch (err) {
      const errorMessage = err?.message || "Erreur lors de la suppression de la catégorie"
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  // Récupérer les catégories populaires
  const getPopularCategories = useCallback(async (limit = 10) => {
    setLoading(true)
    setError(null)
    
    try {
      const response = await api.get(`${endpoints.categoriesPopular}?limit=${limit}`)
      return response
    } catch (err) {
      const errorMessage = err?.message || "Erreur lors de la récupération des catégories populaires"
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  // Rechercher des catégories
  const searchCategories = useCallback(async (query) => {
    setLoading(true)
    setError(null)
    
    try {
      const response = await api.get(`${endpoints.categoriesSearch}?q=${encodeURIComponent(query)}`)
      return response
    } catch (err) {
      const errorMessage = err?.message || "Erreur lors de la recherche de catégories"
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const getDefaultCategory = () => {
    // Retourne la première catégorie disponible depuis l'API
    if (categories.length > 0) {
      return categories[0]
    }
    
    // Pas de fallback, retourne null si aucune catégorie
    return null
  }

  return {
    categories,
    loading,
    error,
    createCategory,
    getCategories,
    getCategory,
    getCategoryByName,
    updateCategory,
    deleteCategory,
    getPopularCategories,
    searchCategories,
    getDefaultCategory
  }
}
